import React, { useEffect, useState, useRef } from 'react';
import '../styles/AIEcosystemTrade.css';

const AIEcosystemTrade = () => {
  const [stockData, setStockData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedStrategy, setSelectedStrategy] = useState('balanced');
  const [investmentAmount, setInvestmentAmount] = useState(10000);
  const [selectedStocks, setSelectedStocks] = useState(new Set());
  const [portfolioResults, setPortfolioResults] = useState(null);
  const [backtestResults, setBacktestResults] = useState(null);
  const [backtestPeriod, setBacktestPeriod] = useState('1y');
  const [showCalculator, setShowCalculator] = useState(false);
  const canvasRef = useRef(null);

  const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000';

  // AI Ecosystem structure with connections
  const ecosystemStructure = {
    core: [
      { symbol: 'NVDA', name: 'NVIDIA', x: 50, y: 20, color: '#76b900', category: 'Core AI' },
      { symbol: 'MSFT', name: 'Microsoft', x: 30, y: 20, color: '#00a4ef', category: 'Core AI' },
      { symbol: 'GOOGL', name: 'Google', x: 70, y: 20, color: '#ea4335', category: 'Core AI' },
      { symbol: 'META', name: 'Meta', x: 20, y: 40, color: '#0a66c2', category: 'Core AI' },
      { symbol: 'TSLA', name: 'Tesla', x: 80, y: 40, color: '#e82127', category: 'Core AI' },
    ],
    dataCenter: [
      { symbol: 'EQIX', name: 'Equinix', x: 15, y: 60, color: '#ff6b35', category: 'Data Centers' },
      { symbol: 'DLR', name: 'Digital Realty', x: 35, y: 65, color: '#f7931e', category: 'Data Centers' },
      { symbol: 'PLD', name: 'Prologis', x: 55, y: 62, color: '#fdb913', category: 'Data Centers' },
      { symbol: 'CCI', name: 'Crown Castle', x: 75, y: 65, color: '#ff7043', category: 'Data Centers' },
    ],
    networking: [
      { symbol: 'AVGO', name: 'Broadcom', x: 10, y: 80, color: '#1f88e5', category: 'Networking' },
      { symbol: 'QCOM', name: 'Qualcomm', x: 30, y: 85, color: '#3953dc', category: 'Networking' },
      { symbol: 'AMD', name: 'AMD', x: 50, y: 82, color: '#ed1c24', category: 'Networking' },
      { symbol: 'VZ', name: 'Verizon', x: 70, y: 85, color: '#f01012', category: 'Networking' },
      { symbol: 'T', name: 'AT&T', x: 85, y: 80, color: '#00a8e1', category: 'Networking' },
    ],
    power: [
      { symbol: 'NEE', name: 'NextEra', x: 20, y: 95, color: '#00a651', category: 'Power' },
      { symbol: 'DUK', name: 'Duke Energy', x: 40, y: 98, color: '#004b87', category: 'Power' },
      { symbol: 'PPL', name: 'PPL Corp', x: 60, y: 95, color: '#003478', category: 'Power' },
      { symbol: 'AQN.TO', name: 'Algonquin', x: 80, y: 98, color: '#1b3d4d', category: 'Power' },
    ],
    cooling: [
      { symbol: 'HON', name: 'Honeywell', x: 35, y: 110, color: '#fc6600', category: 'Cooling' },
      { symbol: 'MMM', name: '3M', x: 55, y: 110, color: '#e4002b', category: 'Cooling' },
      { symbol: 'ITW', name: 'Illinois Tool', x: 75, y: 110, color: '#003f7f', category: 'Cooling' },
    ],
  };

  // Investment strategies
  const strategies = {
    aggressive: {
      name: 'Aggressive Growth',
      description: 'High-risk, high-reward focused on core AI leaders',
      allocation: { core: 80, dataCenter: 10, networking: 10, power: 0, cooling: 0 },
      recommended: ['NVDA', 'MSFT', 'GOOGL', 'META', 'AMD'],
    },
    balanced: {
      name: 'Balanced Portfolio',
      description: 'Diversified across AI stack with moderate risk',
      allocation: { core: 40, dataCenter: 20, networking: 20, power: 10, cooling: 10 },
      recommended: ['NVDA', 'MSFT', 'EQIX', 'AVGO', 'AMD', 'NEE', 'HON'],
    },
    conservative: {
      name: 'Income & Stability',
      description: 'Focus on infrastructure with dividends and stability',
      allocation: { core: 20, dataCenter: 25, networking: 15, power: 25, cooling: 15 },
      recommended: ['MSFT', 'EQIX', 'DLR', 'VZ', 'T', 'NEE', 'DUK', 'HON'],
    },
    infrastructure: {
      name: 'AI Infrastructure',
      description: 'Pure play on AI infrastructure and support',
      allocation: { core: 0, dataCenter: 40, networking: 30, power: 20, cooling: 10 },
      recommended: ['EQIX', 'DLR', 'AVGO', 'QCOM', 'NEE', 'DUK', 'HON'],
    },
  };

  // Define connections between nodes
  const connections = [
    // Core to Data Centers
    { from: 'NVDA', to: 'EQIX', type: 'strong' },
    { from: 'NVDA', to: 'DLR', type: 'strong' },
    { from: 'MSFT', to: 'EQIX', type: 'strong' },
    { from: 'MSFT', to: 'DLR', type: 'medium' },
    { from: 'GOOGL', to: 'PLD', type: 'strong' },
    { from: 'GOOGL', to: 'DLR', type: 'medium' },
    { from: 'META', to: 'CCI', type: 'medium' },
    { from: 'META', to: 'EQIX', type: 'medium' },

    // Core to Networking
    { from: 'NVDA', to: 'AVGO', type: 'strong' },
    { from: 'NVDA', to: 'QCOM', type: 'medium' },
    { from: 'MSFT', to: 'AMD', type: 'strong' },
    { from: 'GOOGL', to: 'AMD', type: 'strong' },
    { from: 'META', to: 'AVGO', type: 'medium' },

    // Data Centers to Networking
    { from: 'EQIX', to: 'AVGO', type: 'medium' },
    { from: 'DLR', to: 'AVGO', type: 'medium' },
    { from: 'PLD', to: 'QCOM', type: 'weak' },

    // Data Centers to Power
    { from: 'EQIX', to: 'NEE', type: 'strong' },
    { from: 'DLR', to: 'DUK', type: 'strong' },
    { from: 'PLD', to: 'PPL', type: 'medium' },
    { from: 'CCI', to: 'DUK', type: 'medium' },

    // Networking to Power
    { from: 'AVGO', to: 'NEE', type: 'medium' },
    { from: 'VZ', to: 'DUK', type: 'medium' },

    // Data Centers to Cooling
    { from: 'EQIX', to: 'HON', type: 'strong' },
    { from: 'DLR', to: 'HON', type: 'strong' },
    { from: 'PLD', to: 'MMM', type: 'medium' },

    // Power to Cooling
    { from: 'NEE', to: 'HON', type: 'weak' },
  ];

  useEffect(() => {
    const fetchStockData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Collect all unique symbols
        const allSymbols = new Set();
        Object.values(ecosystemStructure).forEach(category => {
          category.forEach(stock => allSymbols.add(stock.symbol));
        });

        // Fetch current price data for all stocks
        const promises = Array.from(allSymbols).map(symbol =>
          fetch(`${API_BASE}/api/stock/${symbol}/current`)
            .then(r => r.json())
            .catch(() => ({ symbol, error: true }))
        );

        const results = await Promise.all(promises);

        // Build stock data map
        const dataMap = {};
        results.forEach(data => {
          if (!data.error) {
            dataMap[data.symbol] = {
              price: data.current_price || 0,
              change: data.day_change || 0,
              changePercent: data.day_change_percent || 0,
              timestamp: data.timestamp || new Date().toISOString(),
            };
          }
        });

        setStockData(dataMap);
      } catch (err) {
        console.error('Error fetching stock data:', err);
        setError('Failed to load stock data. Some nodes may not display prices.');
      } finally {
        setLoading(false);
      }
    };

    fetchStockData();

    // Refresh every 30 seconds
    const interval = setInterval(fetchStockData, 30000);
    return () => clearInterval(interval);
  }, []);

  // Draw the ecosystem diagram
  useEffect(() => {
    if (!canvasRef.current || loading) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;

    // Set up canvas size
    const container = canvas.parentElement;
    const width = container.clientWidth;
    const height = container.clientHeight;

    canvas.width = width * dpr;
    canvas.height = height * dpr;
    ctx.scale(dpr, dpr);

    // Clear canvas
    ctx.fillStyle = 'var(--bg-primary)';
    ctx.fillRect(0, 0, width, height);

    // Build node map for faster lookup with padding to prevent text cutoff
    const padding = 100; // pixels padding from canvas edges
    const usableWidth = width - (padding * 2);
    const usableHeight = height - (padding * 2);
    
    const nodeMap = {};
    Object.values(ecosystemStructure).forEach(category => {
      category.forEach(stock => {
        nodeMap[stock.symbol] = {
          ...stock,
          x: padding + (stock.x / 100) * usableWidth,
          y: padding + (stock.y / 100) * usableHeight,
        };
      });
    });

    // Draw connections
    connections.forEach(conn => {
      const fromNode = nodeMap[conn.from];
      const toNode = nodeMap[conn.to];

      if (fromNode && toNode) {
        const isDarkTheme = document.body.classList.contains('dark-theme');
        let lineColor = isDarkTheme ? 'rgba(150, 150, 150, 0.3)' : 'rgba(100, 100, 100, 0.2)';
        let lineWidth = 1;

        if (conn.type === 'strong') {
          lineColor = isDarkTheme ? 'rgba(200, 200, 200, 0.5)' : 'rgba(80, 80, 80, 0.4)';
          lineWidth = 2;
        } else if (conn.type === 'medium') {
          lineColor = isDarkTheme ? 'rgba(170, 170, 170, 0.4)' : 'rgba(100, 100, 100, 0.3)';
          lineWidth = 1.5;
        }

        ctx.strokeStyle = lineColor;
        ctx.lineWidth = lineWidth;
        ctx.beginPath();
        ctx.moveTo(fromNode.x, fromNode.y);
        ctx.lineTo(toNode.x, toNode.y);
        ctx.stroke();
      }
    });

    // Draw nodes
    Object.values(ecosystemStructure).forEach(category => {
      category.forEach(stock => {
        const node = nodeMap[stock.symbol];
        const data = stockData[stock.symbol];
        const isSelected = selectedStocks.has(stock.symbol);

        // Node circle
        const radius = isSelected ? 30 : 25;
        ctx.fillStyle = stock.color;
        ctx.beginPath();
        ctx.arc(node.x, node.y, radius, 0, 2 * Math.PI);
        ctx.fill();

        // Border - highlight selected nodes
        ctx.strokeStyle = isSelected ? 'rgba(255, 215, 0, 0.9)' : 'rgba(255, 255, 255, 0.3)';
        ctx.lineWidth = isSelected ? 4 : 2;
        ctx.stroke();

        // Helper function to draw text with outline for better readability
        const drawOutlinedText = (text, x, y, fontSize, fontWeight = 'normal') => {
          ctx.font = `${fontWeight} ${fontSize}px Arial`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          
          // Draw outline/stroke for better visibility
          ctx.strokeStyle = 'rgba(0, 0, 0, 0.8)';
          ctx.lineWidth = 3;
          ctx.strokeText(text, x, y);
          
          // Draw fill text
          ctx.fillStyle = 'white';
          ctx.fillText(text, x, y);
        };

        // Symbol text
        drawOutlinedText(stock.symbol, node.x, node.y - 8, 12, 'bold');

        // Price and change
        if (data) {
          const changeColor = data.changePercent >= 0 ? '#4CAF50' : '#F44336';
          
          // Price
          ctx.font = '12px Arial';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.strokeStyle = 'rgba(0, 0, 0, 0.8)';
          ctx.lineWidth = 3;
          ctx.strokeText(`$${data.price.toFixed(2)}`, node.x, node.y + 6);
          ctx.fillStyle = 'white';
          ctx.fillText(`$${data.price.toFixed(2)}`, node.x, node.y + 6);

          // Change percentage
          ctx.font = 'bold 11px Arial';
          ctx.strokeStyle = 'rgba(0, 0, 0, 0.8)';
          ctx.lineWidth = 3;
          ctx.strokeText(
            `${data.changePercent >= 0 ? '▲' : '▼'} ${Math.abs(data.changePercent).toFixed(2)}%`,
            node.x,
            node.y + 18
          );
          ctx.fillStyle = changeColor;
          ctx.fillText(
            `${data.changePercent >= 0 ? '▲' : '▼'} ${Math.abs(data.changePercent).toFixed(2)}%`,
            node.x,
            node.y + 18
          );
        }
      });
    });
  }, [stockData, loading]);

  const handleNodeClick = (symbol) => {
    // Toggle stock selection for portfolio
    setSelectedStocks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(symbol)) {
        newSet.delete(symbol);
      } else {
        newSet.add(symbol);
      }
      return newSet;
    });
  };

  const calculatePortfolio = () => {
    if (selectedStocks.size === 0) {
      alert('Please select at least one stock');
      return;
    }

    const selectedData = Array.from(selectedStocks)
      .map(symbol => ({
        symbol,
        price: stockData[symbol]?.price || 0,
        change: stockData[symbol]?.changePercent || 0,
      }))
      .filter(s => s.price > 0);

    if (selectedData.length === 0) {
      alert('No valid price data for selected stocks');
      return;
    }

    const allocPerStock = investmentAmount / selectedData.length;
    const shares = selectedData.map(s => ({
      ...s,
      allocation: allocPerStock,
      shares: Math.floor(allocPerStock / s.price),
      totalValue: Math.floor(allocPerStock / s.price) * s.price,
    }));

    const totalInvested = shares.reduce((sum, s) => sum + s.totalValue, 0);
    const avgChange = shares.reduce((sum, s) => sum + s.change, 0) / shares.length;
    const projectedValue = totalInvested * (1 + avgChange / 100);

    setPortfolioResults({
      stocks: shares,
      totalInvested,
      avgChange,
      projectedValue,
      dailyPnL: projectedValue - totalInvested,
    });
  };

  const runBacktest = async () => {
    if (selectedStocks.size === 0) {
      alert('Please select at least one stock for backtest');
      return;
    }

    setBacktestResults({ loading: true });
    
    try {
      const promises = Array.from(selectedStocks).map(symbol =>
        fetch(`${API_BASE}/api/backtest/${symbol}?period=${backtestPeriod}`)
          .then(r => r.json())
          .catch(() => ({ symbol, error: true }))
      );

      const results = await Promise.all(promises);
      const normalized = results.map(r => {
        if (r?.error) return { symbol: r.symbol, error: true };
        const top = Array.isArray(r.top_parameters) && r.top_parameters[0] ? r.top_parameters[0] : null;
        return {
          symbol: r.symbol,
          metrics: top?.metrics || null,
          params: top?.params || null
        };
      });

      const validResults = normalized.filter(r => r.metrics);

      if (validResults.length === 0) {
        setBacktestResults({ error: 'No backtest data available' });
        return;
      }

      const avgReturn = validResults.reduce((sum, r) => sum + (r.metrics.total_return || 0), 0) / validResults.length;
      const avgWinRate = validResults.reduce((sum, r) => sum + (r.metrics.win_rate || 0), 0) / validResults.length;
      const avgSharpe = validResults.reduce((sum, r) => sum + (r.metrics.sharpe_ratio || 0), 0) / validResults.length;

      setBacktestResults({
        results: validResults,
        summary: {
          avgReturn: avgReturn.toFixed(2),
          avgWinRate: avgWinRate.toFixed(2),
          avgSharpe: avgSharpe.toFixed(2),
          period: backtestPeriod,
        },
      });
    } catch (err) {
      setBacktestResults({ error: 'Backtest failed' });
    }
  };

  const applyStrategy = (strategyKey) => {
    setSelectedStrategy(strategyKey);
    const strategy = strategies[strategyKey];
    const recommended = new Set(strategy.recommended);
    setSelectedStocks(recommended);
  };

  return (
    <div className="ai-ecosystem-container">
      <div className="ecosystem-header">
        <div className="header-content">
          <h1>🤖 AI Ecosystem Trading Platform</h1>
          <p>
            Select stocks, choose strategies, calculate portfolios, and run backtests on the AI industry ecosystem
          </p>
        </div>
        {loading && <div className="loading-badge">Loading prices...</div>}
      </div>

      <div className="ecosystem-main-layout">
        {/* Strategy Selection Panel */}
        <div className="strategy-panel">
          <h3>📊 Investment Strategies</h3>
          <div className="strategy-cards">
            {Object.entries(strategies).map(([key, strategy]) => (
              <div
                key={key}
                className={`strategy-card ${selectedStrategy === key ? 'active' : ''}`}
                onClick={() => applyStrategy(key)}
              >
                <h4>{strategy.name}</h4>
                <p>{strategy.description}</p>
                <div className="allocation-summary">
                  {Object.entries(strategy.allocation)
                    .filter(([_, v]) => v > 0)
                    .map(([cat, pct]) => (
                      <span key={cat} className="allocation-badge">
                        {cat}: {pct}%
                      </span>
                    ))}
                </div>
                <div className="recommended-stocks">
                  <strong>Stocks:</strong> {strategy.recommended.join(', ')}
                </div>
              </div>
            ))}
          </div>

          {/* Portfolio Calculator */}
          <div className="portfolio-calculator">
            <h3>💰 Portfolio Calculator</h3>
            <div className="calculator-controls">
              <label>
                Investment Amount:
                <input
                  type="number"
                  value={investmentAmount}
                  onChange={(e) => setInvestmentAmount(Number(e.target.value))}
                  min="1000"
                  step="1000"
                />
              </label>
              <label>
                Selected Stocks: {selectedStocks.size}
              </label>
              <button onClick={calculatePortfolio} className="btn-primary">
                Calculate Portfolio
              </button>
            </div>

            {portfolioResults && (
              <div className="portfolio-results">
                <h4>Portfolio Analysis</h4>
                <div className="result-summary">
                  <div className="result-item">
                    <span>Total Invested:</span>
                    <strong>${portfolioResults.totalInvested.toFixed(2)}</strong>
                  </div>
                  <div className="result-item">
                    <span>Avg Daily Change:</span>
                    <strong className={portfolioResults.avgChange >= 0 ? 'positive' : 'negative'}>
                      {portfolioResults.avgChange.toFixed(2)}%
                    </strong>
                  </div>
                  <div className="result-item">
                    <span>Daily P&L:</span>
                    <strong className={portfolioResults.dailyPnL >= 0 ? 'positive' : 'negative'}>
                      ${portfolioResults.dailyPnL.toFixed(2)}
                    </strong>
                  </div>
                </div>
                <div className="stocks-breakdown">
                  <h5>Stock Allocation:</h5>
                  <table>
                    <thead>
                      <tr>
                        <th>Symbol</th>
                        <th>Price</th>
                        <th>Shares</th>
                        <th>Total</th>
                        <th>Change</th>
                      </tr>
                    </thead>
                    <tbody>
                      {portfolioResults.stocks.map(s => (
                        <tr key={s.symbol}>
                          <td><strong>{s.symbol}</strong></td>
                          <td>${s.price.toFixed(2)}</td>
                          <td>{s.shares}</td>
                          <td>${s.totalValue.toFixed(2)}</td>
                          <td className={s.change >= 0 ? 'positive' : 'negative'}>
                            {s.change.toFixed(2)}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Backtest Section */}
          <div className="backtest-section">
            <h3>📈 Backtest Analysis</h3>
            <div className="backtest-controls">
              <label>
                Period:
                <select value={backtestPeriod} onChange={(e) => setBacktestPeriod(e.target.value)}>
                  <option value="1mo">1 Month</option>
                  <option value="3mo">3 Months</option>
                  <option value="6mo">6 Months</option>
                  <option value="1y">1 Year</option>
                  <option value="2y">2 Years</option>
                </select>
              </label>
              <button onClick={runBacktest} className="btn-secondary">
                Run Backtest
              </button>
            </div>

            {backtestResults && backtestResults.loading && (
              <div className="loading-message">Running backtest...</div>
            )}

            {backtestResults && backtestResults.error && (
              <div className="error-message">{backtestResults.error}</div>
            )}

            {backtestResults && backtestResults.summary && (
              <div className="backtest-results">
                <h4>Backtest Results ({backtestResults.summary.period})</h4>
                <div className="result-summary">
                  <div className="result-item">
                    <span>Avg Return:</span>
                    <strong className={backtestResults.summary.avgReturn >= 0 ? 'positive' : 'negative'}>
                      {backtestResults.summary.avgReturn}%
                    </strong>
                  </div>
                  <div className="result-item">
                    <span>Avg Win Rate:</span>
                    <strong>{backtestResults.summary.avgWinRate}%</strong>
                  </div>
                  <div className="result-item">
                    <span>Avg Sharpe:</span>
                    <strong>{backtestResults.summary.avgSharpe}</strong>
                  </div>
                </div>
                <div className="backtest-details">
                  <h5>Individual Results:</h5>
                  <table>
                    <thead>
                      <tr>
                        <th>Symbol</th>
                        <th>Return</th>
                        <th>Win Rate</th>
                        <th>Sharpe</th>
                        <th>Max DD</th>
                      </tr>
                    </thead>
                    <tbody>
                      {backtestResults.results.map(r => (
                        <tr key={r.symbol}>
                          <td><strong>{r.symbol}</strong></td>
                          <td className={r.metrics.total_return >= 0 ? 'positive' : 'negative'}>
                            {r.metrics.total_return?.toFixed(2)}%
                          </td>
                          <td>{r.metrics.win_rate?.toFixed(2)}%</td>
                          <td>{r.metrics.sharpe_ratio?.toFixed(2)}</td>
                          <td className="negative">{r.metrics.max_drawdown?.toFixed(2)}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Visualization Panel */}
        <div className="visualization-panel">
          <h3>🗺️ AI Ecosystem Map - Click to Select</h3>
          <p className="instruction">Click on any node to add/remove from your portfolio</p>
          
          <div className="selected-stocks-bar">
            <strong>Selected ({selectedStocks.size}):</strong>
            {Array.from(selectedStocks).map(symbol => (
              <span key={symbol} className="selected-badge" onClick={() => handleNodeClick(symbol)}>
                {symbol} ×
              </span>
            ))}
            {selectedStocks.size > 0 && (
              <button onClick={() => setSelectedStocks(new Set())} className="btn-clear">
                Clear All
              </button>
            )}
          </div>

          <div className="canvas-container">
            <canvas
              ref={canvasRef}
              className="ecosystem-canvas"
              onClick={(e) => {
                const canvas = canvasRef.current;
                const rect = canvas.getBoundingClientRect();
                const padding = 100;
                const usableWidth = canvas.width / (window.devicePixelRatio || 1) - (padding * 2);
                const usableHeight = canvas.height / (window.devicePixelRatio || 1) - (padding * 2);
                const x = e.clientX - rect.left;
                const y = e.clientY - rect.top;

                // Check if click is on a node
                Object.values(ecosystemStructure).forEach(category => {
                  category.forEach(stock => {
                    const nodeX = padding + (stock.x / 100) * usableWidth;
                    const nodeY = padding + (stock.y / 100) * usableHeight;
                    const distance = Math.sqrt((x - nodeX) ** 2 + (y - nodeY) ** 2);
                    if (distance < 30) {
                      handleNodeClick(stock.symbol);
                    }
                  });
                });
              }}
            />
          </div>

          <div className="ecosystem-legend-compact">
            <div className="legend-item">
              <span className="legend-dot" style={{ backgroundColor: '#76b900' }}></span>
              <span>Core AI</span>
            </div>
            <div className="legend-item">
              <span className="legend-dot" style={{ backgroundColor: '#ff6b35' }}></span>
              <span>Data Centers</span>
            </div>
            <div className="legend-item">
              <span className="legend-dot" style={{ backgroundColor: '#1f88e5' }}></span>
              <span>Networking</span>
            </div>
            <div className="legend-item">
              <span className="legend-dot" style={{ backgroundColor: '#00a651' }}></span>
              <span>Power</span>
            </div>
            <div className="legend-item">
              <span className="legend-dot" style={{ backgroundColor: '#fc6600' }}></span>
              <span>Cooling</span>
            </div>
          </div>

          <div className="stocks-table">
            <h4>All Stocks</h4>
            <table>
              <thead>
                <tr>
                  <th>Symbol</th>
                  <th>Name</th>
                  <th>Price</th>
                  <th>Change</th>
                  <th>Category</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {Object.values(ecosystemStructure).flat().map(stock => {
                  const data = stockData[stock.symbol];
                  const isSelected = selectedStocks.has(stock.symbol);
                  return (
                    <tr key={stock.symbol} className={isSelected ? 'selected-row' : ''}>
                      <td><strong>{stock.symbol}</strong></td>
                      <td>{stock.name}</td>
                      <td>{data ? `$${data.price.toFixed(2)}` : '-'}</td>
                      <td className={data && data.changePercent >= 0 ? 'positive' : 'negative'}>
                        {data ? `${data.changePercent.toFixed(2)}%` : '-'}
                      </td>
                      <td>{stock.category}</td>
                      <td>
                        <button
                          onClick={() => handleNodeClick(stock.symbol)}
                          className={isSelected ? 'btn-remove' : 'btn-add'}
                        >
                          {isSelected ? 'Remove' : 'Add'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {error && (
        <div className="error-message">
          ⚠️ {error}
        </div>
      )}
    </div>
  );
};

export default AIEcosystemTrade;
